"use strict";

var _Object$defineProperty = require("@babel/runtime-corejs3/core-js-stable/object/define-property");
var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
_Object$defineProperty(exports, "__esModule", {
  value: true
});
exports.syncDeepLinkParamsToPatientRelAndPurpose = exports.makeConfig = exports.machineJSON = exports.createMainMachine = exports.createMachine = exports.canSkipApptInfoWithDeepLinkParams = void 0;
var _includes = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/instance/includes"));
var _xstate = require("xstate");
var _States = require("../../lib/constants/States");
var _covidVaccineUtils = require("../../lib/covidVaccineUtils");
var _steps = require("../../lib/constants/steps");
var _utils = require("../../lib/utils");
var _gxUtils = require("../../lib/gxUtils");
var _epicOSWUtils = require("../../lib/epicOSWUtils");
var _getInitialContext = _interopRequireDefault(require("../getInitialContext"));
var _apptInfo = require("./apptInfo");
var _apptTime = require("./apptTime");
var _patientInfo = require("./patientInfo");
var _reviewAppt = require("./reviewAppt");
var _provider = require("./provider");
var _apptSummary = require("./apptSummary");
var _oswApptInfo = require("./oswApptInfo");
var _Actions = require("../../lib/constants/Actions");
var _deepLinking = require("../../lib/constants/deepLinking");
var _Filters = require("../../lib/constants/Filters");
/**
 * Creates and returns a machine based on the step passed in
 *
 * @param {String} step
 * @param {MachineContext} ctx
 */
const createMachine = (step, ctx) => {
  const {
    providerId,
    provider,
    insuranceOptions,
    locations,
    appointmentQuery,
    selectedAppointment,
    patientRegistration,
    csrfToken,
    isCaptchaVerified,
    patientRel,
    purpose,
    locationId,
    periodStart,
    ehr,
    epicOSWConfig,
    linkedGxAnswers,
    gx,
    useProviderInsuranceOnRegForm,
    covidVaccinePatientRegFormConfig,
    patientRegFormConfig,
    hidePatientRelConfig,
    ehrPatientList,
    services,
    selectedPatientIndex,
    customerCode,
    updatedFormFields,
    deepLinkingParams,
    visibility,
    dbEnabledProperty,
    productName,
    apptTimeFilters,
    bookedAppt
  } = ctx;
  const machines = {
    [_States.loadingProvider]: () => (0, _provider.createProviderMachine)({
      providerId,
      insuranceOptions,
      epicOSWConfig,
      covidVaccinePatientRegFormConfig,
      patientRegFormConfig,
      ehr,
      services,
      ehrPatientList,
      customerCode,
      productName,
      gx,
      useProviderInsuranceOnRegForm,
      purpose,
      deepLinkingParams,
      visibility,
      dbEnabledProperty
    }),
    [_States.apptInfo]: () => (0, _apptInfo.createApptInfoMachine)({
      provider,
      isCaptchaVerified,
      csrfToken,
      patientRel,
      purpose,
      appointmentQuery,
      gx,
      hidePatientRelConfig,
      customerCode,
      visibility
    }),
    [_States.apptTime]: () => (0, _apptTime.createApptTimeMachine)({
      apptTimeFilters,
      provider,
      locations,
      appointmentQuery,
      locationId,
      periodStart,
      isCaptchaVerified,
      customerCode,
      csrfToken,
      visibility,
      services,
      productName
    }),
    [_States.patientInfo]: () => (0, _patientInfo.createPatientInfoMachine)({
      provider,
      insuranceOptions,
      appointmentQuery,
      selectedAppointment,
      updatedFields: {
        ...updatedFormFields,
        ...(0, _gxUtils.formFieldsFromGxAnswers)(linkedGxAnswers)
      },
      covidVaccinePatientRegFormConfig,
      patientRegFormConfig,
      isCaptchaVerified,
      csrfToken,
      ehr,
      ehrPatientList,
      selectedPatientIndex,
      services,
      customerCode
    }),
    [_States.reviewAppt]: () => (0, _reviewAppt.createReviewApptMachine)({
      provider,
      selectedAppointment,
      patientRegistration,
      csrfToken,
      customerCode,
      linkedGxAnswers,
      productName
    }),
    [_States.apptSummary]: () => (0, _apptSummary.createApptSummaryMachine)({
      bookedAppt,
      provider,
      selectedAppointment
    }),
    [_States.oswApptInfo]: () => (0, _oswApptInfo.createOSWApptInfoMachine)({
      provider,
      epicOSWConfig
    })
  };
  return machines[step]();
};

/**
 * A new service should be created if it doesn't exist or
 * if we want to reset its current state e.g:
 *  - apptTime should always be new, to avoid stale appointment data
 * @param {String} step
 * @param {MachineContext} ctx
 */
exports.createMachine = createMachine;
const shouldCreateNewService = _ref => {
  let {
    step,
    ctx
  } = _ref;
  const alwaysNewServiceSteps = [_States.apptInfo, _States.apptTime, _States.reviewAppt, _States.patientInfo];
  return (0, _includes.default)(alwaysNewServiceSteps).call(alwaysNewServiceSteps, step) || !ctx.steps[step];
};

/**
 * Everytime we go into the `apptTime`, we'll be re-fetching appointments
 * and the currently selected appointment may not exist anymore
 * @param {String} step
 */
const shouldResetSelectedAppt = step => {
  const steps = [_States.apptTime, _States.apptInfo];
  return (0, _includes.default)(steps).call(steps, step);
};

/**
 * Creates a service for the given step and a updates the current context
 * @param {String} step
 */
const createService = step => (0, _xstate.assign)(ctx => {
  if (!shouldCreateNewService({
    step,
    ctx
  })) {
    return {
      ...ctx,
      curStep: step
    };
  }
  return {
    ...ctx,
    curStep: step,
    selectedAppointment: shouldResetSelectedAppt(step) ? null : ctx.selectedAppointment,
    steps: {
      ...ctx.steps,
      [step]: (0, _xstate.spawn)(createMachine(step, ctx))
    }
  };
});

// --------
// Actions
// --------

/**
 * Parses context and returns what should be used as `patientRel`
 * - try to pull it from deep link params
 * - use a default patient relationship type based on customer config
 * - return what is in ctx
 */
const parsePatientRelFromCtx = ctx => {
  const {
    deepLinkingParams,
    hidePatientRelConfig,
    provider
  } = ctx;
  if ((0, _includes.default)(_deepLinking.VALID_PATIENT_RELATIONSHIPS).call(_deepLinking.VALID_PATIENT_RELATIONSHIPS, deepLinkingParams.patientRel)) {
    return deepLinkingParams.patientRel;
  }
  if ((0, _utils.hidePatientRel)(provider, hidePatientRelConfig)) {
    return _Filters.DEFAULT_PATIENT_REL;
  }
  return ctx.patientRel;
};

/**
 * Sync any data from Deep linking params and assign values to the following fields in context:
 * - purpose
 * - patientRel
 * - appointmentQuery
 */
const syncDeepLinkParamsToPatientRelAndPurpose = ctx => {
  const {
    deepLinkingParams,
    provider
  } = ctx;
  const changes = {
    patientRel: parsePatientRelFromCtx(ctx)
  };
  if (changes.patientRel && deepLinkingParams.purpose) {
    // We'll set purpose either way and only set appointmentQuery if the purpose has availability
    // If there isn't availability, the purpose will be auto-selected with an "unavailable to book" banner
    changes.purpose = {
      ...ctx.purpose,
      name: deepLinkingParams.purpose
    };
    const providerPurposes = provider.availableApptPurposes;
    if (providerPurposes[changes.patientRel].has(changes.purpose.name)) {
      changes.appointmentQuery = {
        patientRel: changes.patientRel,
        purpose: changes.purpose
      };
    }
  }
  return changes;
};
exports.syncDeepLinkParamsToPatientRelAndPurpose = syncDeepLinkParamsToPatientRelAndPurpose;
const syncParamsFromDeepLink = ctx => {
  const {
    deepLinkingParams: {
      locationId,
      periodStart
    }
  } = ctx;
  return {
    ...syncDeepLinkParamsToPatientRelAndPurpose(ctx),
    ...(locationId && {
      locationId
    }),
    ...(periodStart && {
      periodStart: (0, _utils.formatApiDate)(periodStart)
    })
  };
};
const actions = {
  createApptInfoService: createService(_States.apptInfo),
  createApptTimeService: createService(_States.apptTime),
  createProviderService: createService(_States.loadingProvider),
  createPatientInfoService: createService(_States.patientInfo),
  createReviewApptService: createService(_States.reviewAppt),
  createApptSummaryService: createService(_States.apptSummary),
  createOSWApptInfoService: createService(_States.oswApptInfo),
  setAppointmentQuery: (0, _xstate.assign)({
    appointmentQuery: (ctx, ev) => ({
      patientRel: ev.data.patientRel,
      purpose: ev.data.purpose
    })
  }),
  setProvider: (0, _xstate.assign)({
    provider: (ctx, ev) => ev.data.provider
  }),
  setInsuranceOptions: (0, _xstate.assign)({
    insuranceOptions: (ctx, ev) => ev.data.insuranceOptions
  }),
  setLocations: (0, _xstate.assign)({
    locations: (ctx, ev) => ev.data.locations
  }),
  setIsCaptchaVerified: (0, _xstate.assign)({
    isCaptchaVerified: (ctx, ev) => ev.data.isCaptchaVerified
  }),
  setGx: (0, _xstate.assign)({
    gx: (ctx, ev) => {
      // This will set keys like gx.v1 & gx.v2, and we need to include keys set prior in ctx.gx like contextualInfo
      return {
        ...ctx.gx,
        ...ev.data.gx
      };
    }
  }),
  setLinkedGxAnswers: (0, _xstate.assign)({
    linkedGxAnswers: (ctx, ev) => ev.data.linkedGxAnswers
  }),
  setSelectedAppointment: (0, _xstate.assign)({
    selectedAppointment: (ctx, ev) => ev.data.selectedAppointment
  }),
  setEhrPatientList: (0, _xstate.assign)({
    ehrPatientList: (_ctx, ev) => ev.data.ehrPatientList
  }),
  setSelectedPatientIndex: (0, _xstate.assign)({
    selectedPatientIndex: (_, ev) => ev.data.selectedPatientIndex
  }),
  setPatientRegistration: (0, _xstate.assign)({
    patientRegistration: (_, ev) => ev.data.patientRegistration
  }),
  setApptTimeFilters: (0, _xstate.assign)({
    apptTimeFilters: (_ref2, _ref3) => {
      let {
        apptTimeFilters
      } = _ref2;
      let {
        data = {}
      } = _ref3;
      return {
        ...apptTimeFilters,
        ...data
      };
    }
  }),
  setBookedAppt: (0, _xstate.assign)({
    bookedAppt: (ctx, ev) => ev.data.bookedAppt
  }),
  setStepsConfigurationType: (0, _xstate.assign)({
    stepsConfigurationType: ctx => {
      const {
        provider
      } = ctx;
      return (0, _covidVaccineUtils.isCovidVaccineProvider)(provider) && !ctx.covidUseDefaultStepOrder ? _steps.STEPS_PMC_COVID_VACCINE : _steps.STEPS_PMC_DEFAULT;
    }
  }),
  syncParamsFromDeepLink: (0, _xstate.assign)(ctx => {
    return syncParamsFromDeepLink(ctx);
  }),
  updateFormFields: (0, _xstate.assign)({
    updatedFormFields: (_ctx, ev) => ev.data.updatedFields
  }),
  clearApptInfoDeepLinkingParams: (0, _xstate.assign)(ctx => {
    return {
      deepLinkingParams: {
        ...ctx.deepLinkingParams,
        purpose: null,
        patientRel: null
      }
    };
  }),
  // reset location id deeplink param
  resetLocationIdInCtx: (0, _xstate.assign)(() => {
    const {
      locationId
    } = (0, _getInitialContext.default)();
    return {
      locationId
    };
  }),
  // reset period start deeplink param
  resetPeriodStartInCtx: (0, _xstate.assign)(() => {
    const {
      periodStart
    } = (0, _getInitialContext.default)();
    return {
      periodStart
    };
  }),
  // if needed, implementation for this should be passed in when creating this machine
  onDidBook: ctx => {
    ctx.onDidBook && ctx.onDidBook({
      appointment: ctx.bookedAppt,
      provider: ctx.provider,
      patientRegistration: ctx.patientRegistration,
      location: (0, _utils.getApptLocation)(ctx.provider, ctx.selectedAppointment)
    });
  },
  updateApptPurpose: (0, _xstate.assign)({
    purpose: (ctx, ev) => ({
      ...ctx.purpose,
      ...ev.data.purpose
    })
  })
};

// --------
// Guards
// --------

const hasGxQuestions = ctx => Boolean(ctx.gx.v1 || ctx.gx.v2 || ctx.gx.v2Conditionals);
const canSkipApptInfoWithDeepLinkParams = ctx => {
  const {
    deepLinkingParams: {
      patientRel,
      purpose
    },
    provider
  } = ctx;

  // if we have gx questions enabled we can't move forward
  if (hasGxQuestions(ctx)) {
    return false;
  }
  return (0, _includes.default)(_deepLinking.VALID_PATIENT_RELATIONSHIPS).call(_deepLinking.VALID_PATIENT_RELATIONSHIPS, patientRel) && provider.availableApptPurposes[patientRel].has(purpose);
};
exports.canSkipApptInfoWithDeepLinkParams = canSkipApptInfoWithDeepLinkParams;
const guards = {
  hasApptInfoDeepLinkingParams: ctx => {
    const {
      deepLinkingParams: {
        purpose,
        patientRel
      }
    } = ctx;
    return Boolean(purpose || patientRel);
  },
  isGotoApptInfo: (ctx, ev) => ev.data.step === _States.apptInfo,
  isGotoApptTime: (ctx, ev) => ev.data.step === _States.apptTime,
  isGotoPatientInfo: (ctx, ev) => ev.data.step === _States.patientInfo,
  isValidGotoReviewAppt: (ctx, ev) =>
  // only allow goto review when we have a selected appointment
  ev.data.step === _States.reviewAppt && !!ctx.selectedAppointment,
  shouldShowApptSummary: ctx => ctx.showApptSummary,
  isOSWEnabled: ctx => {
    const {
      provider,
      epicOSWConfig
    } = ctx;
    return (0, _epicOSWUtils.isOSWEnabled)(provider, epicOSWConfig);
  },
  hasNoSelectedAppointment: ctx => !ctx.selectedAppointment,
  hasBookedAppointment: ctx => ctx.bookedAppt,
  // is the next step (relative to curStep) patienInfo according to the current stepsConfiguration?
  isNextStepPatientInfo: ctx => (0, _utils.getNextStepId)(ctx.stepsConfigurationType, ctx.curStep) === _States.patientInfo,
  // is the next step (relative to curStep) apptTime according to the current stepsConfiguration?
  isNextStepApptTime: ctx => (0, _utils.getNextStepId)(ctx.stepsConfigurationType, ctx.curStep) === _States.apptTime,
  // is the next step (relative to curStep) reviewAppt according to the current stepsConfiguration?
  isNextStepReviewAppt: ctx => (0, _utils.getNextStepId)(ctx.stepsConfigurationType, ctx.curStep) === _States.reviewAppt,
  shouldPatientInfoRedirectToApptTime: ctx => {
    // patient info should redirect to appt time if appt time comes before patient info
    // and there is no appointment selected. This could happen during browser "forward" navigation
    // after going back to appt time where the selected appointment got cleared
    const isApptTimePrevious = (0, _utils.getStepIndex)(ctx.stepsConfigurationType, ctx.curStep) > (0, _utils.getStepIndex)(ctx.stepsConfigurationType, _States.apptTime);
    return isApptTimePrevious && !ctx.selectedAppointment;
  },
  shouldApptInfoSkipToApptTime: ctx => {
    // appt info can skip to appt time when the appt time step is next from app info and we have deeplinking params
    const apptInfoIndex = (0, _utils.getStepIndex)(ctx.stepsConfigurationType, _States.apptInfo);
    const apptTimeIndex = (0, _utils.getStepIndex)(ctx.stepsConfigurationType, _States.apptTime);
    if (apptTimeIndex !== apptInfoIndex + 1) {
      // if apptTime doesn't immediately follow apptInfo, bail out now
      return false;
    }
    // check if we have valid deeplinking params
    return canSkipApptInfoWithDeepLinkParams(ctx);
  },
  shouldApptInfoSkipToPatientInfo: ctx => {
    // appt info can skip to patient info when the appt time step is next from app info and we have deeplinking params
    const apptInfoIndex = (0, _utils.getStepIndex)(ctx.stepsConfigurationType, _States.apptInfo);
    const patientInfoIndex = (0, _utils.getStepIndex)(ctx.stepsConfigurationType, _States.patientInfo);
    if (patientInfoIndex !== apptInfoIndex + 1) {
      // if patientInfo doesn't immediately follow apptInfo, bail out now
      return false;
    }
    return canSkipApptInfoWithDeepLinkParams(ctx);
  }
};
const machineJSON = () => ({
  id: 'Machine__main',
  initial: _States.initializing,
  // NOTE: this context is expected to be overriden
  // when this machine gets intialized
  // @see getInitialContext
  context: {},
  on: {
    [_Actions.GO_BACK]: [
    // 'back' stays on appt summary if we have a booked appt
    {
      target: _States.apptSummary,
      cond: 'hasBookedAppointment'
    }, {
      target: _States.apptInfo,
      cond: 'isGotoApptInfo'
    }, {
      target: _States.apptTime,
      cond: 'isGotoApptTime'
    }, {
      target: _States.patientInfo,
      cond: 'isGotoPatientInfo'
    }, {
      target: _States.reviewAppt,
      cond: 'isValidGotoReviewAppt'
    }],
    [_Actions.REBOOK]: {
      target: _States.apptTime
    }
  },
  states: {
    [_States.initializing]: {
      on: {
        [_Actions.INITIALIZED]: _States.loadingProvider
      }
    },
    [_States.loadingProvider]: {
      entry: 'createProviderService',
      on: {
        [_Actions.NEXT]: {
          target: _States.routing,
          actions: ['setProvider', 'setInsuranceOptions', 'setLocations', 'setIsCaptchaVerified', 'setGx', 'setStepsConfigurationType', 'setEhrPatientList', 'setSelectedPatientIndex', 'updateApptPurpose']
        }
      }
    },
    [_States.routing]: {
      initial: _States.checkingIsOSWEnabled,
      states: {
        [_States.checkingIsOSWEnabled]: {
          always: [{
            cond: 'isOSWEnabled',
            target: `#${_States.oswApptInfo}`
          }, {
            target: _States.deepLinking
          }]
        },
        [_States.deepLinking]: {
          always: [{
            actions: ['syncParamsFromDeepLink'],
            target: `#${_States.apptInfo}`
          }]
        }
      }
    },
    [_States.oswApptInfo]: {
      id: _States.oswApptInfo,
      entry: 'createOSWApptInfoService'
    },
    [_States.apptInfo]: {
      id: _States.apptInfo,
      entry: 'createApptInfoService',
      always: [{
        cond: 'shouldApptInfoSkipToApptTime',
        actions: ['clearApptInfoDeepLinkingParams'],
        target: _States.apptTime
      }, {
        cond: 'shouldApptInfoSkipToPatientInfo',
        actions: ['clearApptInfoDeepLinkingParams'],
        target: _States.patientInfo
      }, {
        cond: 'hasApptInfoDeepLinkingParams',
        actions: ['clearApptInfoDeepLinkingParams']
      }],
      on: {
        [_Actions.NEXT]: [{
          cond: 'isNextStepApptTime',
          target: _States.apptTime,
          actions: ['setIsCaptchaVerified', 'setLinkedGxAnswers', 'setAppointmentQuery']
        }, {
          cond: 'isNextStepPatientInfo',
          target: _States.patientInfo,
          actions: ['setIsCaptchaVerified', 'setLinkedGxAnswers', 'setAppointmentQuery']
        }]
      }
    },
    [_States.apptTime]: {
      id: _States.apptTime,
      entry: ['createApptTimeService'],
      on: {
        [_Actions.CAPTCHA_VERIFIED]: {
          actions: 'setIsCaptchaVerified'
        },
        [_Actions.RESET_LOCATION_FILTER]: {
          actions: ['resetLocationIdInCtx']
        },
        [_Actions.SET_FILTERS_ON_PARENT]: {
          actions: ['setApptTimeFilters']
        },
        [_Actions.NEXT]: [{
          cond: 'isNextStepReviewAppt',
          target: _States.reviewAppt,
          actions: [
          /* this enables a user to select a new appointment by going back in the flow
             if periodStart continued to live in ctx
             you would not be able to move backward to apptTime from patientInfo in the deepLinking use case */
          'resetPeriodStartInCtx', 'setSelectedAppointment']
        }, {
          cond: 'isNextStepPatientInfo',
          target: _States.patientInfo,
          actions: [
          /* this enables a user to select a new appointment by going back in the flow
             if periodStart continued to live in ctx
             you would not be able to move backward to apptTime from patientInfo in the deepLinking use case */
          'resetPeriodStartInCtx', 'setSelectedAppointment']
        }]
      }
    },
    [_States.patientInfo]: {
      entry: 'createPatientInfoService',
      // if we landed on patient info without an appointment, send user back to
      // apptTime to select one
      // only if the next step is reviewAppt, otherwise apptTime is the next step
      always: [{
        cond: 'shouldPatientInfoRedirectToApptTime',
        target: `#${_States.apptTime}`
      }],
      on: {
        [_Actions.FORM_UPDATE]: {
          actions: 'updateFormFields'
        },
        [_Actions.NEXT]: [{
          cond: 'isNextStepApptTime',
          target: _States.apptTime,
          actions: 'setPatientRegistration'
        }, {
          cond: 'isNextStepReviewAppt',
          target: _States.reviewAppt,
          actions: 'setPatientRegistration'
        }]
      }
    },
    [_States.reviewAppt]: {
      entry: 'createReviewApptService',
      initial: _States.idle,
      states: {
        [_States.idle]: {},
        [_States.routing]: {
          always: [{
            cond: 'shouldShowApptSummary',
            target: _States.done
          }, {
            target: _States.didBook
          }]
        },
        [_States.didBook]: {},
        [_States.done]: {
          type: 'final'
        }
      },
      on: {
        [_Actions.NEXT]: {
          target: `.${_States.routing}`,
          actions: ['setBookedAppt', 'onDidBook']
        }
      },
      onDone: _States.apptSummary
    },
    [_States.apptSummary]: {
      entry: 'createApptSummaryService'
    }
  }
});
exports.machineJSON = machineJSON;
const makeConfig = () => ({
  actions,
  guards
});
exports.makeConfig = makeConfig;
const createMainMachine = _ref4 => {
  let {
    context,
    config = makeConfig()
  } = _ref4;
  return (0, _xstate.Machine)(machineJSON()).withConfig(config).withContext(context);
};
exports.createMainMachine = createMainMachine;
createMachine.__docgenInfo = {
  "description": "Creates and returns a machine based on the step passed in\n\n@param {String} step\n@param {MachineContext} ctx",
  "methods": [],
  "displayName": "createMachine"
};